import '../../App.scss'
import CommunitySection from './Components/CommunitySection/CommunitySection'
import CoveredSection from './Components/CoveredSection/CoveredSection'
import FaqSection from '../../components/FaqSection/FaqSection'
import FooterSection from '../../components/FooterSection/FooterSection'
import FullScreenVideoSection from './Components/FullScreenVideoSection/FullScreenVideoSection'
import PhotoWallSection from './Components/PhotoWallSection/PhotoWallSection'
import SecondaryVideoSection from './Components/SecondaryVideoSection/SecondaryVideoSection'
import SecondaryVideoGallery from './Components/SecondaryVideoSection/SecondaryVideoGallery'
import SeenOnSection from './Components/SeenOnSection/SeenOnSection'
import TitleSection from './Components/TitleSection/TitleSection'
import { TILE_FAQS } from '../../components/FaqSection/consts'
import * as S from './HomePageUI.styles'
import useScreenSize from '../../hooks/useScreenSize'
import { useState, useMemo } from 'react'
import MenuButton from '../../elements/TopBar/Buttons/MenuButton'
import MixtilesLogo from '../../elements/TopBar/Logo/MixtilesLogo'
import { useTranslation } from 'react-i18next'
import { useKeys } from 'services/KeysProvider'
import { getPricingCountry } from 'services/CountryManager'
import { HOMEPAGE_CONFIG } from './homePage.config'

type Props = {
  ctaProps: {
    ctaText: string
    onClick: () => void
  }
  heroVideoCldId: string
}

export default function HomePageUI({ ctaProps, heroVideoCldId }: Props) {
  const { t } = useTranslation()
  const isMediumScreen = useScreenSize('md')
  const [showStickyTopBar, setShowStickyTopBar] = useState(false)
  const keys = useKeys()
  const pricingCountry = getPricingCountry(keys.ipCountry)

  const communityVideos = useMemo(
    () => HOMEPAGE_CONFIG.getCommunityVideos(t, pricingCountry),
    [t, pricingCountry]
  )

  const fullscreenVideoCldId = isMediumScreen
    ? HOMEPAGE_CONFIG.qualityVideo.desktop
    : HOMEPAGE_CONFIG.qualityVideo.mobile

  return (
    <S.Main>
      {isMediumScreen && (
        <S.StickyTopBar
          initial={{
            translateY: -80,
          }}
          animate={{
            translateY: showStickyTopBar ? 0 : -80,
          }}
          transition={{
            duration: 0.2,
          }}
        >
          <S.TopBarLeftContainer>
            <MenuButton testId="menu-button-header" />
            <MixtilesLogo />
          </S.TopBarLeftContainer>
          <S.StickyTopBarCta onClick={ctaProps.onClick}>
            {ctaProps.ctaText}
          </S.StickyTopBarCta>
        </S.StickyTopBar>
      )}
      <S.WidthCapList $fullscreen>
        <TitleSection
          ctaProps={ctaProps}
          heroVideoCldId={heroVideoCldId}
          onVisibilityChange={(visibilityRatio) =>
            setShowStickyTopBar(
              isMediumScreen ? visibilityRatio <= 0.1 : visibilityRatio <= 0.5
            )
          }
        />
        <SeenOnSection />
        {isMediumScreen ? (
          <SecondaryVideoGallery videos={HOMEPAGE_CONFIG.desktopVideos} />
        ) : (
          <SecondaryVideoSection videos={HOMEPAGE_CONFIG.mobileVideos} />
        )}
        <FullScreenVideoSection
          cldId={fullscreenVideoCldId}
          videoText={
            isMediumScreen
              ? t('homepage.fullscreen_title_desktop')
              : t('homepage.fullscreen_title')
          }
        />
      </S.WidthCapList>
      {!isMediumScreen && (
        <S.WallContainer>
          <S.WidthCapList>
            <PhotoWallSection />
          </S.WidthCapList>
        </S.WallContainer>
      )}

      <S.CommunityContainer>
        <S.WidthCapList>
          <CommunitySection items={communityVideos} />
        </S.WidthCapList>
      </S.CommunityContainer>

      <S.EndingContainer>
        <S.WidthCapList>
          <CoveredSection
            illustrationNames={HOMEPAGE_CONFIG.illustrations.names}
            mobileIllustrationUrls={HOMEPAGE_CONFIG.illustrations.mobileUrls}
          />
          <FaqSection faqs={TILE_FAQS} source="Homepage" />
        </S.WidthCapList>
      </S.EndingContainer>

      <S.FooterContainer>
        <S.WidthCapList>
          <FooterSection />
        </S.WidthCapList>
      </S.FooterContainer>
    </S.Main>
  )
}
